// ./src/components/layouts/PrintLayout/PrintLayout.tsx
import React from "react";
import { Box, Group, Text, Paper, Stack } from "@mantine/core";
import { useNavigate } from "react-router-dom";

import { Logo } from "@/components/generic/Logo";
import { AppButton } from "@/components/generic";

export interface PrintLayoutProps {
  children: React.ReactNode;
  /** Document title displayed in header */
  title?: string;
  /** Subtitle displayed below title */
  subtitle?: string;
  /** Whether to show FormSubmits logo (false for Business plan) */
  showLogo?: boolean;
  /** Optional controls to display in the options panel (hidden when printing) */
  optionsPanel?: React.ReactNode;
}

export const PrintLayout: React.FC<PrintLayoutProps> = ({
  children,
  title,
  subtitle,
  showLogo = true,
  optionsPanel,
}) => {
  const navigate = useNavigate();

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        backgroundColor: "#fff",
      }}
    >
      {/* Header - hidden when printing */}
      <Box
        className="print-hide"
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          backgroundColor: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Group h={70} px="md" justify="space-between">
          <Group gap="md">
            {showLogo && <Logo size="small" />}
            {title && (
              <Box>
                <Text size="lg" fw={600} c="dark">
                  {title}
                </Text>
                {subtitle && (
                  <Text size="sm" c="dimmed">
                    {subtitle}
                  </Text>
                )}
              </Box>
            )}
          </Group>

          <Group gap="sm">
            <AppButton
              variant="secondary"
              iconName="ArrowLeft"
              onClick={handleBack}
            >
              Back
            </AppButton>
            <AppButton
              variant="primary"
              iconName="Printer"
              onClick={handlePrint}
            >
              Print
            </AppButton>
          </Group>
        </Group>
      </Box>

      {/* Options panel - hidden when printing */}
      {optionsPanel && (
        <Box
          className="print-hide"
          px="md"
          py="md"
          style={{
            borderBottom: "1px solid var(--mantine-color-gray-3)",
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
        >
          <Paper p="md" radius="md" withBorder>
            <Stack gap="md">
              <Text fw={600} size="sm">
                Print Options
              </Text>
              {optionsPanel}
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Main content - this is what gets printed */}
      <Box
        p="md"
        style={{
          maxWidth: "210mm", // A4 width
          margin: "0 auto",
        }}
      >
        {children}
      </Box>

      {/* Print-specific styles */}
      <style>
        {`
          @media print {
            /* Hide UI controls when printing */
            .print-hide {
              display: none !important;
            }

            /* Reset page styling for print */
            body {
              background: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            /* Page margins */
            @page {
              margin: 15mm;
              size: A4;
            }

            /* Ensure content fills page */
            html, body {
              height: auto !important;
              overflow: visible !important;
            }

            /* Optimize typography for print */
            body {
              font-size: 11pt;
              line-height: 1.4;
            }

            /* Prevent orphaned headers */
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
              break-after: avoid;
            }

            /* Keep field groups together */
            .print-field-group {
              break-inside: avoid;
              page-break-inside: avoid;
            }

            /* Keep sections together when possible */
            .print-section {
              break-inside: avoid;
              page-break-inside: avoid;
            }

            /* Force page break before element */
            .print-page-break-before {
              break-before: page;
              page-break-before: always;
            }

            /* Remove shadows and unnecessary styling */
            * {
              box-shadow: none !important;
            }

            /* Ensure links don't show URL */
            a {
              text-decoration: none !important;
              color: inherit !important;
            }

            /* Hide any scrollbars */
            ::-webkit-scrollbar {
              display: none;
            }
          }

          /* Screen styles for print preview */
          @media screen {
            .print-preview-container {
              background-color: #f5f5f5;
              min-height: 100vh;
            }
          }
        `}
      </style>
    </Box>
  );
};
